import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Building2, Users, FileText, CheckCircle, Clock, AlertCircle,
  Activity, Eye, Copy, TrendingUp, UserPlus, MapPin, Search, Loader2,
  Plus, Settings, BarChart3, PieChart, XCircle, CheckCheck, Trash2, Briefcase,
  IndianRupee, UserCog, ListChecks
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [societies, setSocieties] = useState([]);
  const [societyRequests, setSocietyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSociety, setSelectedSociety] = useState(null);
  const [activities, setActivities] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [prefeasibilityDialogOpen, setPrefeasibilityDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [approvingClaim, setApprovingClaim] = useState(null);
  const [verifyingSociety, setVerifyingSociety] = useState(null);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [deletingSociety, setDeletingSociety] = useState(null);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [twoFAVerified, setTwoFAVerified] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [salesAdmins, setSalesAdmins] = useState([]);
  const [newSalesAdmin, setNewSalesAdmin] = useState({ email: "", name: "" });
  const [addingSalesAdmin, setAddingSalesAdmin] = useState(false);
  const [analysts, setAnalysts] = useState([]);
  const [newAnalyst, setNewAnalyst] = useState({ email: "", name: "" });
  const [addingAnalyst, setAddingAnalyst] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [prefeasibilityResult, setPrefeasibilityResult] = useState(null);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  
  const [newSociety, setNewSociety] = useState({
    name: "",
    address: "",
    location_lat: null,
    location_lng: null,
    total_flats: "",
    plot_area: "",
    year_built: ""
  });

  useEffect(() => {
    fetchData();
    loadGoogleMapsScript();
  }, []);

  // Initialize autocomplete when dialog opens and google is loaded
  useEffect(() => {
    if (createDialogOpen && googleLoaded) {
      // Small delay to ensure input is rendered
      const timer = setTimeout(() => {
        initAutocomplete();
      }, 100);
      return () => clearTimeout(timer);
    }
    // Clean up autocomplete when dialog closes
    if (!createDialogOpen && autocompleteRef.current) {
      window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, [createDialogOpen, googleLoaded]);

  const loadGoogleMapsScript = () => {
    if (window.google?.maps?.places) {
      setGoogleLoaded(true);
      return;
    }
    
    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setGoogleLoaded(true));
      return;
    }
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    script.onerror = () => toast.error("Failed to load Google Maps");
    document.head.appendChild(script);
  };

  const initAutocomplete = () => {
    if (!window.google?.maps?.places || !inputRef.current) {
      console.log("Google Maps or input not ready");
      return;
    }
    
    // Clear existing autocomplete if any
    if (autocompleteRef.current) {
      try {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      } catch (e) {}
      autocompleteRef.current = null;
    }
    
    try {
      // Use the existing Autocomplete API (still works despite deprecation warning)
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "in" },
        fields: ["formatted_address", "geometry", "name"]
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        console.log("Place selected:", place);
        
        if (place && place.geometry) {
          setNewSociety(prev => ({
            ...prev,
            address: place.formatted_address || place.name || "",
            location_lat: place.geometry.location.lat(),
            location_lng: place.geometry.location.lng()
          }));
          toast.success("Location selected!");
        } else if (place && place.name) {
          setNewSociety(prev => ({
            ...prev,
            address: place.name
          }));
        }
      });
      
      // Fix: Prevent dialog from closing when clicking on autocomplete suggestions
      // The pac-container is appended to body, so we need to stop propagation
      const pacContainerHandler = (e) => {
        e.stopPropagation();
      };
      
      // Add event listeners to pac-container when it appears
      const observer = new MutationObserver((mutations) => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          pacContainer.addEventListener('mousedown', pacContainerHandler);
          pacContainer.addEventListener('touchstart', pacContainerHandler);
        }
      });
      
      observer.observe(document.body, { childList: true, subtree: true });
      
      // Store observer for cleanup
      inputRef.current._observer = observer;
      
      console.log("Autocomplete initialized successfully");
    } catch (error) {
      console.error("Error initializing autocomplete:", error);
      toast.error("Failed to initialize address search");
    }
  };

  const fetchData = async () => {
    try {
      // Check if user is master admin
      const adminCheckRes = await fetch(`${API}/auth/is-master-admin`, { credentials: "include" });
      if (adminCheckRes.ok) {
        const adminData = await adminCheckRes.json();
        setIsMasterAdmin(adminData.is_master_admin);
        setIsPlatformAdmin(adminData.is_platform_admin);

        if (adminData.is_platform_admin) {
          const auditRes = await fetch(`${API}/admin/audit-logs`, { credentials: "include" });
          if (auditRes.ok) setAuditLogs(await auditRes.json());
          const statusRes = await fetch(`${API}/admin/2fa/status`, { credentials: "include" });
          if (statusRes.ok) { const s = await statusRes.json(); setTwoFAVerified(!!s.verified); }
        }
        
        // If master admin, fetch society requests, service requests & sales admins
        if (adminData.is_master_admin) {
          const [requestsRes, serviceRes, salesRes, analystRes] = await Promise.all([
            fetch(`${API}/society-requests`, { credentials: "include" }),
            fetch(`${API}/admin/service-requests`, { credentials: "include" }),
            fetch(`${API}/admin/sales-admins`, { credentials: "include" }),
            fetch(`${API}/admin/feasibility-analysts`, { credentials: "include" })
          ]);
          if (requestsRes.ok) setSocietyRequests(await requestsRes.json());
          if (serviceRes.ok) setServiceRequests(await serviceRes.json());
          if (salesRes.ok) setSalesAdmins(await salesRes.json());
          if (analystRes.ok) setAnalysts(await analystRes.json());
        }
      }

      const [statsRes, societiesRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: "include" }),
        fetch(`${API}/admin/societies`, { credentials: "include" })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (societiesRes.ok) setSocieties(await societiesRes.json());
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async (societyId) => {
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/activity`, { credentials: "include" });
      if (res.ok) {
        setActivities(await res.json());
        setSelectedSociety(societyId);
      }
    } catch (error) {
      toast.error("Failed to load activities");
    }
  };

  const copyClaimCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Claim code copied!");
  };

  const updateServiceStatus = async (sessionId, status) => {
    try {
      const res = await fetch(`${API}/admin/service-requests/${sessionId}/status?status=${status}`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        setServiceRequests(serviceRequests.map(r => r.session_id === sessionId ? { ...r, status } : r));
        toast.success(`Marked as ${status.replace("_", " ")}`);
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("Failed to update status");
    }
  };

  const addSalesAdmin = async (e) => {
    e.preventDefault();
    if (!newSalesAdmin.email) return;
    setAddingSalesAdmin(true);
    try {
      const res = await fetch(`${API}/admin/sales-admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newSalesAdmin.email, name: newSalesAdmin.name || null }),
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        setSalesAdmins([...salesAdmins, data]);
        setNewSalesAdmin({ email: "", name: "" });
        toast.success("Sales admin added — they now have free demo access");
      } else {
        toast.error(data.detail || "Failed to add sales admin");
      }
    } catch {
      toast.error("Failed to add sales admin");
    } finally {
      setAddingSalesAdmin(false);
    }
  };

  const removeSalesAdmin = async (email) => {
    try {
      const res = await fetch(`${API}/admin/sales-admins/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setSalesAdmins(salesAdmins.filter(a => a.email !== email));
        toast.success("Sales admin removed");
      } else {
        toast.error("Failed to remove sales admin");
      }
    } catch {
      toast.error("Failed to remove sales admin");
    }
  };

  const addAnalyst = async (e) => {
    e.preventDefault();
    if (!newAnalyst.email) return;
    setAddingAnalyst(true);
    try {
      const res = await fetch(`${API}/admin/feasibility-analysts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newAnalyst.email, name: newAnalyst.name || null }),
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        setAnalysts([...analysts, data]);
        setNewAnalyst({ email: "", name: "" });
        toast.success("Feasibility Analyst added");
      } else {
        toast.error(data.detail || "Failed to add analyst");
      }
    } catch {
      toast.error("Failed to add analyst");
    } finally {
      setAddingAnalyst(false);
    }
  };

  const removeAnalyst = async (email) => {
    try {
      const res = await fetch(`${API}/admin/feasibility-analysts/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setAnalysts(analysts.filter(a => a.email !== email));
        toast.success("Analyst removed");
      } else {
        toast.error("Failed to remove analyst");
      }
    } catch {
      toast.error("Failed to remove analyst");
    }
  };


  const handleCreateSociety = async (e) => {
    e.preventDefault();
    if (!newSociety.name || !newSociety.address) {
      toast.error("Please fill in name and address");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API}/societies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newSociety.name,
          address: newSociety.address,
          location_lat: newSociety.location_lat,
          location_lng: newSociety.location_lng,
          total_flats: newSociety.total_flats ? parseInt(newSociety.total_flats) : 0,
          plot_area: newSociety.plot_area ? parseFloat(newSociety.plot_area) : null,
          year_built: newSociety.year_built ? parseInt(newSociety.year_built) : null
        }),
        credentials: "include"
      });

      if (res.ok) {
        const result = await res.json();
        if (result.status === "pending_approval") {
          // Non-master-admin - request submitted
          toast.success("Society creation request submitted for admin approval!");
        } else {
          // Master admin - society created directly
          toast.success(`Society created! Claim code: ${result.claim_code}`);
          if (result.claim_code) {
            navigator.clipboard.writeText(result.claim_code);
          }
        }
        setCreateDialogOpen(false);
        setNewSociety({ name: "", address: "", location_lat: null, location_lng: null, total_flats: "", plot_area: "", year_built: "" });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create society");
      }
    } catch (error) {
      toast.error("Failed to create society");
    } finally {
      setCreating(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    setApprovingRequest(requestId);
    try {
      const res = await fetch(`${API}/society-requests/${requestId}/approve`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Society request approved and created!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to approve request");
      }
    } catch (error) {
      toast.error("Failed to approve request");
    } finally {
      setApprovingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to reject this society creation request?")) return;
    
    setApprovingRequest(requestId);
    try {
      const res = await fetch(`${API}/society-requests/${requestId}/reject?reason=Rejected by admin`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Society request rejected");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to reject request");
      }
    } catch (error) {
      toast.error("Failed to reject request");
    } finally {
      setApprovingRequest(null);
    }
  };

  const runPrefeasibility = async (society) => {
    if (!society.location_lat || !society.location_lng) {
      toast.error("Society has no location data. Please update the address.");
      return;
    }

    setAnalyzing(true);
    setPrefeasibilityResult(null);
    setPrefeasibilityDialogOpen(true);

    try {
      const res = await fetch(`${API}/prefeasibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          location_lat: society.location_lat,
          location_lng: society.location_lng,
          plot_area: society.plot_area,
          building_age: society.year_built ? new Date().getFullYear() - society.year_built : null,
          total_flats: society.total_flats
        }),
        credentials: "include"
      });

      if (res.ok) {
        const data = await res.json();
        setPrefeasibilityResult({ ...data, society });
        
        // Update society with prefeasibility status
        await fetch(`${API}/societies/${society.society_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...society,
            prefeasibility_done: true,
            prefeasibility_viable: data.analysis?.toLowerCase().includes("viable") || true
          }),
          credentials: "include"
        });
        
        toast.success("Pre-feasibility analysis complete");
      } else {
        toast.error("Failed to run pre-feasibility analysis");
      }
    } catch (error) {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case "claim": return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case "member_registration": return <UserPlus className="w-4 h-4 text-blue-600" />;
      case "member_approved": return <Users className="w-4 h-4 text-emerald-600" />;
      case "member_rejected": return <AlertCircle className="w-4 h-4 text-red-600" />;
      case "document_upload": return <FileText className="w-4 h-4 text-purple-600" />;
      default: return <Activity className="w-4 h-4 text-slate-600" />;
    }
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    const sections = text.split(/\n\n+/);
    return sections.map((section, idx) => {
      if (section.startsWith('#')) {
        return <h3 key={idx} className="text-lg font-bold text-slate-900 mt-4 mb-2">{section.replace(/^#+\s*/, '')}</h3>;
      }
      if (section.includes('\n-') || section.startsWith('-')) {
        const items = section.split('\n').filter(Boolean);
        return (
          <ul key={idx} className="list-disc list-inside space-y-1 text-slate-700 mb-3 text-sm">
            {items.map((item, i) => <li key={i}>{item.replace(/^-\s*/, '')}</li>)}
          </ul>
        );
      }
      return <p key={idx} className="text-slate-700 text-sm mb-3">{section}</p>;
    });
  };

  const handleApproveClaim = async (societyId) => {
    setApprovingClaim(societyId);
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/approve-claim`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Claim approved successfully!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to approve claim");
      }
    } catch (error) {
      toast.error("Failed to approve claim");
    } finally {
      setApprovingClaim(null);
    }
  };

  const handleRejectClaim = async (societyId) => {
    if (!window.confirm("Are you sure you want to reject this claim request?")) return;
    
    setApprovingClaim(societyId);
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/reject-claim?reason=Rejected by admin`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Claim rejected");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to reject claim");
      }
    } catch (error) {
      toast.error("Failed to reject claim");
    } finally {
      setApprovingClaim(null);
    }
  };

  const handleVerify = async (societyId) => {
    setVerifyingSociety(societyId);
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/verify`, { method: "PUT", credentials: "include" });
      if (res.ok) { toast.success("Society verified — services unlocked"); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed to verify"); }
    } catch { toast.error("Failed to verify"); }
    finally { setVerifyingSociety(null); }
  };

  const handleRejectVerification = async (societyId) => {
    if (!window.confirm("Reject this society's verification request?")) return;
    setVerifyingSociety(societyId);
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/reject-verification?reason=Rejected by team`, { method: "PUT", credentials: "include" });
      if (res.ok) { toast.success("Verification rejected"); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed to reject"); }
    } catch { toast.error("Failed to reject"); }
    finally { setVerifyingSociety(null); }
  };

  const viewProof = async (societyId) => {
    try {
      const res = await fetch(`${API}/admin/societies/${societyId}/verification-proof`, { credentials: "include" });
      if (!res.ok) { toast.error("No proof uploaded"); return; }
      const data = await res.json();
      const w = window.open("", "_blank");
      if (w) {
        if ((data.mime || "").startsWith("image/")) {
          w.document.write(`<title>${data.name || "Proof"}</title><img src="${data.data_url}" style="max-width:100%"/>`);
        } else {
          w.location.href = data.data_url;
        }
      }
    } catch { toast.error("Failed to load proof"); }
  };

  const handleDeleteSociety = async (society) => {    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${society.name}"?\n\nThis will permanently delete:\n- All documents\n- All members\n- All votes and consent records\n- All activity logs\n- All 79A process data\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    setDeletingSociety(society.society_id);
    try {
      const res = await fetch(`${API}/societies/${society.society_id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (res.ok) {
        toast.success(`Society "${society.name}" deleted successfully`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete society");
      }
    } catch (error) {
      toast.error("Failed to delete society");
    } finally {
      setDeletingSociety(null);
    }
  };

  const getClaimStatusBadge = (society) => {
    if (society.claim_status === "pending_approval") {
      return <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>;
    }
    if (society.is_claimed) {
      return <Badge className="bg-emerald-100 text-emerald-700">Claimed</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">Unclaimed</Badge>;
  };

  const getVerificationBadge = (society) => {
    const v = society.verification_status || "verified";
    if (v === "verified") return <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>;
    if (v === "pending_verification") return <Badge className="bg-amber-100 text-amber-700">Pending Verification</Badge>;
    if (v === "rejected") return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    return <Badge className="bg-slate-100 text-slate-600">Unverified</Badge>;
  };

  const request2FA = async () => {
    setOtpBusy(true);
    try {
      const res = await fetch(`${API}/admin/2fa/request`, { method: "POST", credentials: "include" });
      const d = await res.json();
      if (res.ok) { setOtpSent(true); toast.success(`Code sent to ${d.email}`); }
      else toast.error(d.detail || "Failed to send code");
    } catch { toast.error("Failed to send code"); }
    finally { setOtpBusy(false); }
  };

  const verify2FA = async () => {
    setOtpBusy(true);
    try {
      const res = await fetch(`${API}/admin/2fa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: otpCode }),
        credentials: "include"
      });
      const d = await res.json();
      if (res.ok) { setTwoFAVerified(true); setOtpCode(""); setOtpSent(false); toast.success("Admin verified"); fetchData(); }
      else toast.error(d.detail || "Invalid code");
    } catch { toast.error("Verification failed"); }
    finally { setOtpBusy(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (isPlatformAdmin && !twoFAVerified) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto py-16" data-testid="admin-2fa-gate">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Settings className="w-7 h-7 text-emerald-700" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Admin verification required</h2>
              <p className="text-sm text-slate-600 mb-6">For security, admin access requires a one-time code sent to your email.</p>
              {!otpSent ? (
                <Button onClick={request2FA} disabled={otpBusy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="send-2fa-code-btn">
                  {otpBusy ? "Sending..." : "Email me a code"}
                </Button>
              ) : (
                <div className="space-y-3">
                  <input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="w-full text-center tracking-[0.5em] text-lg border border-slate-300 rounded-lg px-3 py-2"
                    data-testid="2fa-code-input"
                  />
                  <Button onClick={verify2FA} disabled={otpBusy || otpCode.length < 6} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="verify-2fa-btn">
                    {otpBusy ? "Verifying..." : "Verify"}
                  </Button>
                  <button onClick={request2FA} disabled={otpBusy} className="text-xs text-emerald-700 hover:underline">Resend code</button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Admin Dashboard
              </h1>
              {isMasterAdmin && (
                <Badge className="bg-purple-100 text-purple-700">Master Admin</Badge>
              )}
            </div>
            <p className="text-slate-600">
              {isMasterAdmin 
                ? "Create societies, manage requests, and track all activities" 
                : "Request new societies and manage your existing ones"}
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            // Don't close if clicking on pac-container (Google autocomplete dropdown)
            if (!open) {
              const pacContainer = document.querySelector('.pac-container');
              if (pacContainer && pacContainer.style.display !== 'none') {
                // Autocomplete is visible, don't close
                return;
              }
            }
            setCreateDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="create-society-btn">
                <Plus className="w-4 h-4 mr-2" />
                {isMasterAdmin ? "Create Society" : "Request Society"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" onPointerDownOutside={(e) => {
              // Prevent closing when clicking on Google autocomplete dropdown
              if (e.target.closest('.pac-container')) {
                e.preventDefault();
              }
            }} onInteractOutside={(e) => {
              // Prevent closing when interacting with Google autocomplete dropdown
              if (e.target.closest('.pac-container')) {
                e.preventDefault();
              }
            }}>
              <DialogHeader>
                <DialogTitle>
                  {isMasterAdmin ? "Create New Society" : "Request New Society"}
                </DialogTitle>
              </DialogHeader>
              {!isMasterAdmin && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
                  Your request will be reviewed by the master admin. You'll receive an email once approved.
                </div>
              )}
              <form onSubmit={handleCreateSociety} className="space-y-4">
                <div>
                  <Label htmlFor="name">Society Name *</Label>
                  <Input
                    id="name"
                    value={newSociety.name}
                    onChange={(e) => setNewSociety({ ...newSociety, name: e.target.value })}
                    placeholder="e.g., Sunrise CHS Ltd"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="address">Address (Search Location) *</Label>
                  <Input
                    id="address"
                    ref={inputRef}
                    value={newSociety.address}
                    onChange={(e) => setNewSociety({ ...newSociety, address: e.target.value })}
                    placeholder="Start typing address..."
                    required
                    autoComplete="off"
                    className="pac-target-input"
                  />
                  {googleLoaded ? (
                    newSociety.location_lat ? (
                      <p className="text-xs text-emerald-600 mt-1">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        Location: {newSociety.location_lat.toFixed(4)}, {newSociety.location_lng.toFixed(4)}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">Type to search and select from suggestions</p>
                    )
                  ) : (
                    <p className="text-xs text-amber-600 mt-1">Loading Google Maps...</p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="total_flats">Total Flats</Label>
                    <Input
                      id="total_flats"
                      type="number"
                      value={newSociety.total_flats}
                      onChange={(e) => setNewSociety({ ...newSociety, total_flats: e.target.value })}
                      placeholder="48"
                    />
                  </div>
                  <div>
                    <Label htmlFor="plot_area">Plot Area (sq.m)</Label>
                    <Input
                      id="plot_area"
                      type="number"
                      value={newSociety.plot_area}
                      onChange={(e) => setNewSociety({ ...newSociety, plot_area: e.target.value })}
                      placeholder="2500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="year_built">Year Built</Label>
                    <Input
                      id="year_built"
                      type="number"
                      value={newSociety.year_built}
                      onChange={(e) => setNewSociety({ ...newSociety, year_built: e.target.value })}
                      placeholder="1985"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Society
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
              <CardContent className="p-4 text-center">
                <Building2 className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">{stats.total_societies}</p>
                <p className="text-sm text-slate-500">Total Societies</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-600">{stats.claimed_societies}</p>
                <p className="text-sm text-slate-500">Claimed</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-amber-600">{stats.unclaimed_societies}</p>
                <p className="text-sm text-slate-500">Unclaimed</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-blue-600">{stats.total_members}</p>
                <p className="text-sm text-slate-500">Total Members</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-purple-600">{stats.total_documents}</p>
                <p className="text-sm text-slate-500">Documents</p>
              </CardContent>
            </Card>
            <Card className={`bg-gradient-to-br from-orange-50 to-orange-100 ${stats.pending_registrations > 0 ? "ring-2 ring-orange-400" : ""}`}>
              <CardContent className="p-4 text-center">
                <UserPlus className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-orange-600">{stats.pending_registrations}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </CardContent>
            </Card>
            {isMasterAdmin && stats.total_revenue != null && (
              <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 col-span-2 md:col-span-1" data-testid="revenue-card">
                <CardContent className="p-4 text-center">
                  <IndianRupee className="w-6 h-6 text-emerald-100 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">₹{(stats.revenue_this_month || 0).toLocaleString("en-IN")}</p>
                  <p className="text-xs text-emerald-100">This Month • Total ₹{(stats.total_revenue || 0).toLocaleString("en-IN")} ({stats.paid_orders} orders)</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Pending Society Requests (Master Admin Only) */}
        {isMasterAdmin && societyRequests.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Clock className="w-5 h-5" />
                Pending Society Requests ({societyRequests.length})
              </CardTitle>
              <CardDescription>Review and approve/reject society creation requests from users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {societyRequests.map((request) => (
                  <div key={request.request_id} className="bg-white p-4 rounded-lg border border-amber-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{request.name}</h4>
                        <p className="text-sm text-slate-500">{request.address}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Requested by: {request.requested_by_name} ({request.requested_by_email})
                        </p>
                        {request.total_flats > 0 && (
                          <p className="text-xs text-slate-400">{request.total_flats} flats</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveRequest(request.request_id)}
                          disabled={approvingRequest === request.request_id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {approvingRequest === request.request_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCheck className="w-4 h-4 mr-1" />
                          )}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectRequest(request.request_id)}
                          disabled={approvingRequest === request.request_id}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Paid Service Requests (Master Admin Only) */}
        {isMasterAdmin && (
          <Card className="border-emerald-200" data-testid="service-requests-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <IndianRupee className="w-5 h-5" />
                Paid Service Requests ({serviceRequests.length})
              </CardTitle>
              <CardDescription>Architect feasibility & admin-assisted 79A orders paid by societies</CardDescription>
            </CardHeader>
            <CardContent>
              {serviceRequests.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No paid service requests yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Society</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Buyer</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {serviceRequests.map((req) => (
                        <TableRow key={req.session_id} data-testid={`service-request-row-${req.session_id}`}>
                          <TableCell>
                            <p className="font-medium text-slate-900">{req.society_name}</p>
                            <p className="text-xs text-slate-400">{req.society_address}</p>
                          </TableCell>
                          <TableCell className="text-sm">{req.package_name}</TableCell>
                          <TableCell className="font-medium">₹{(req.amount || 0).toLocaleString("en-IN")}</TableCell>
                          <TableCell className="text-sm">{req.email}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              req.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                              req.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                              "bg-amber-100 text-amber-700"
                            }>
                              {(req.status || "pending").replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {req.status !== "in_progress" && req.status !== "completed" && (
                                <Button size="sm" variant="outline" onClick={() => updateServiceStatus(req.session_id, "in_progress")} data-testid={`start-service-${req.session_id}`}>
                                  Start
                                </Button>
                              )}
                              {req.status !== "completed" && (
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateServiceStatus(req.session_id, "completed")} data-testid={`complete-service-${req.session_id}`}>
                                  <CheckCheck className="w-3 h-3 mr-1" />
                                  Done
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sales Admins (Master Admin Only) */}
        {isMasterAdmin && (
          <Card className="border-purple-200" data-testid="sales-admins-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <UserCog className="w-5 h-5" />
                Sales Admins ({salesAdmins.length})
              </CardTitle>
              <CardDescription>Sales admins get free access to all paid features for client demos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={addSalesAdmin} className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  placeholder="sales.person@gmail.com"
                  value={newSalesAdmin.email}
                  onChange={(e) => setNewSalesAdmin({ ...newSalesAdmin, email: e.target.value })}
                  className="flex-1"
                  required
                  data-testid="sales-admin-email-input"
                />
                <Input
                  placeholder="Name (optional)"
                  value={newSalesAdmin.name}
                  onChange={(e) => setNewSalesAdmin({ ...newSalesAdmin, name: e.target.value })}
                  className="flex-1"
                  data-testid="sales-admin-name-input"
                />
                <Button type="submit" disabled={addingSalesAdmin} className="bg-purple-600 hover:bg-purple-700" data-testid="add-sales-admin-btn">
                  {addingSalesAdmin ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Sales Admin
                </Button>
              </form>
              {salesAdmins.length > 0 && (
                <div className="space-y-2">
                  {salesAdmins.map((admin) => (
                    <div key={admin.email} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100" data-testid={`sales-admin-${admin.email}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{admin.name || admin.email}</p>
                        {admin.name && <p className="text-xs text-slate-500">{admin.email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-100 text-purple-700">Demo Access</Badge>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => removeSalesAdmin(admin.email)} data-testid={`remove-sales-admin-${admin.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Feasibility Analysts (Master Admin Only) */}
        {isMasterAdmin && (
          <Card className="border-indigo-200" data-testid="feasibility-analysts-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-700">
                <UserCog className="w-5 h-5" />
                Feasibility Analysts ({analysts.length})
              </CardTitle>
              <CardDescription>Analysts can open the Feasibility Comparison Studio to prepare & sign off detailed feasibility reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={addAnalyst} className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  placeholder="analyst@mangohouse.co.in"
                  value={newAnalyst.email}
                  onChange={(e) => setNewAnalyst({ ...newAnalyst, email: e.target.value })}
                  className="flex-1"
                  required
                  data-testid="analyst-email-input"
                />
                <Input
                  placeholder="Name (optional)"
                  value={newAnalyst.name}
                  onChange={(e) => setNewAnalyst({ ...newAnalyst, name: e.target.value })}
                  className="flex-1"
                  data-testid="analyst-name-input"
                />
                <Button type="submit" disabled={addingAnalyst} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-analyst-btn">
                  {addingAnalyst ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Analyst
                </Button>
              </form>
              {analysts.length > 0 && (
                <div className="space-y-2">
                  {analysts.map((a) => (
                    <div key={a.email} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100" data-testid={`analyst-${a.email}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{a.name || a.email}</p>
                        {a.name && <p className="text-xs text-slate-500">{a.email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-indigo-100 text-indigo-700">Studio Access</Badge>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => removeAnalyst(a.email)} data-testid={`remove-analyst-${a.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Audit Log (Platform Admins) */}
        {isPlatformAdmin && (
          <Card data-testid="admin-audit-log-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ListChecks className="w-5 h-5" />
                Admin Audit Log
              </CardTitle>
              <CardDescription>Every admin action (claim approvals, rejections) — who, what, when</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-sm text-slate-500">No admin actions logged yet.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-auto">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-sm border-b border-slate-100 pb-2" data-testid="audit-log-row">
                      <div>
                        <span className="font-medium text-slate-800">{log.action}</span>
                        <span className="text-slate-500"> — {log.metadata?.society_name || log.target || ""}</span>
                        <div className="text-xs text-slate-400">by {log.actor_email}</div>
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Societies List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              All Societies
            </CardTitle>
            <CardDescription>Manage societies, run pre-feasibility analysis, and track claims</CardDescription>
          </CardHeader>
          <CardContent>
            {societies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No societies created yet</p>
                <p className="text-sm text-slate-400 mt-1">Click "Create Society" to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Society</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verification</TableHead>
                      <TableHead>Claim Code</TableHead>
                      <TableHead>Pre-feasibility</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Readiness</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {societies.map((society) => (
                      <TableRow key={society.society_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{society.name}</p>
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">{society.address}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getClaimStatusBadge(society)}
                          {society.claim_status === "pending_approval" && (
                            <div className="mt-1 text-xs text-slate-500">
                              {society.claimed_by_name} ({society.claimed_by_email})
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getVerificationBadge(society)}
                            {society.verification_status === "pending_verification" && (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => viewProof(society.society_id)} className="text-blue-600 hover:bg-blue-50 h-7 px-2" title="View uploaded proof" data-testid={`view-proof-btn-${society.society_id}`}>
                                  <Eye className="w-4 h-4 mr-1" />Proof
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleVerify(society.society_id)} disabled={verifyingSociety === society.society_id} className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" title="Verify Society" data-testid={`verify-btn-${society.society_id}`}>
                                  {verifyingSociety === society.society_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCheck className="w-4 h-4 mr-1" />Verify</>}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleRejectVerification(society.society_id)} disabled={verifyingSociety === society.society_id} className="text-red-600 hover:bg-red-50 h-7 px-2" title="Reject Verification">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {society.claim_code && society.claim_status !== "approved" && !society.is_claimed ? (
                            <div className="flex items-center gap-2">
                              <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">{society.claim_code}</code>
                              <button onClick={() => copyClaimCode(society.claim_code)} className="hover:bg-slate-100 p-1 rounded">
                                <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {society.prefeasibility_done ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Done
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runPrefeasibility(society)}
                              className="text-emerald-600 hover:bg-emerald-50"
                              disabled={analyzing}
                            >
                              <Search className="w-3 h-3 mr-1" />
                              Analyze
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{society.stats?.members_count || 0}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-600 h-2 rounded-full transition-all"
                                style={{ width: `${society.readiness_score || 0}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{society.readiness_score || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {society.claim_status === "pending_approval" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleApproveClaim(society.society_id)}
                                  disabled={approvingClaim === society.society_id}
                                  className="text-emerald-600 hover:bg-emerald-50"
                                  title="Approve Claim"
                                >
                                  {approvingClaim === society.society_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCheck className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRejectClaim(society.society_id)}
                                  disabled={approvingClaim === society.society_id}
                                  className="text-red-600 hover:bg-red-50"
                                  title="Reject Claim"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {isMasterAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/documents?society=${society.society_id}`)}
                                  title="Upload Documents for this Society"
                                  className="text-blue-600 hover:bg-blue-50"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/builders?society=${society.society_id}`)}
                                  title="Manage Builders for this Society"
                                  className="text-purple-600 hover:bg-purple-50"
                                >
                                  <Building2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/pmc?society=${society.society_id}`)}
                                  title="Manage PMC for this Society"
                                  className="text-orange-600 hover:bg-orange-50"
                                >
                                  <Briefcase className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadActivities(society.society_id)}
                              title="View Activity"
                            >
                              <Activity className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/registrations/${society.society_id}`)}
                              title="Manage Registrations"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSociety(society)}
                              disabled={deletingSociety === society.society_id}
                              className="text-red-600 hover:bg-red-50"
                              title="Delete Society"
                              data-testid={`delete-society-${society.society_id}`}
                            >
                              {deletingSociety === society.society_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Log Section */}
        {selectedSociety && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Activity Log
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedSociety(null)}>
                Close
              </Button>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No activity yet</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {activities.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      {getActivityIcon(activity.action)}
                      <div className="flex-1">
                        <p className="text-sm text-slate-700">{activity.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pre-feasibility Result Dialog */}
        <Dialog open={prefeasibilityDialogOpen} onOpenChange={setPrefeasibilityDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Pre-feasibility Analysis
                {prefeasibilityResult?.society && (
                  <span className="text-slate-500 font-normal text-sm ml-2">
                    - {prefeasibilityResult.society.name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            {analyzing ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-4" />
                <p className="text-slate-600">Analyzing location data...</p>
                <p className="text-sm text-slate-400">This may take 30-60 seconds</p>
              </div>
            ) : prefeasibilityResult ? (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <Badge className="bg-emerald-100 text-emerald-700">Zone: {prefeasibilityResult.zone}</Badge>
                  <Badge className="bg-blue-100 text-blue-700">
                    {prefeasibilityResult.lat?.toFixed(4)}, {prefeasibilityResult.lng?.toFixed(4)}
                  </Badge>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  {formatAnalysis(prefeasibilityResult.analysis)}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
